/**
 * Stage IG redesign Phase 3:投稿管理(spec §9)。
 *
 * 構成(spec §9.1):
 *   - Hero(渐变标题 + Subnav + status filter chips + 検索 + タイプ + 新規投稿按钮)
 *   - 4 列 content grid(§9.2)
 *   - 投稿クリックで大型 detail modal(§9.3)
 *     - 左:メディア preview(画像 object-fit:contain / 動画 native controls)
 *     - 右:caption / hashtag / 公開時間 / タイプ / permalink / performance
 *
 * 投稿 detail modal は **画像と動画どちらも正しく表示** することを最優先。
 * carousel は backend media_items 配列で対応。
 */

import { useState } from "react";
import {
  Calendar,
  ExternalLink,
  FileImage,
  Heart,
  Image as ImageIcon,
  MessageCircle,
  PlayCircle,
  Plus,
  Search,
  X,
} from "lucide-react";

import { instagramApi } from "../../api/instagram";
import {
  AccountChip,
  EmptyState,
  ErrorState,
  InstagramSubnav,
  LoadingState,
  PostMediaBadge,
  RefreshButton,
  SectionCard,
} from "./components/common";
import { InstagramPostComposerModal } from "./components/InstagramPostComposerModal";
import { useInstagramDrafts, useInstagramMedia } from "./hooks/useInstagram";
import type { ContentDraft, MediaItem, MediaType } from "./types";
import { formatFullDateTime, formatNumber } from "./utils/format";
import "./instagram.css";

const MEDIA_TYPE_LABEL: Record<MediaType, string> = {
  IMAGE: "画像",
  VIDEO: "動画",
  REEL: "リール",
  CAROUSEL_ALBUM: "カルーセル",
  STORY: "ストーリー",
};

// ---------------------------------------------------------------------------
// Post card(spec §9.2)
// ---------------------------------------------------------------------------

function PostCard({
  item,
  onClick,
}: {
  item: MediaItem;
  onClick: () => void;
}) {
  const isVideo = item.media_type === "REEL" || item.media_type === "VIDEO";
  return (
    <article className="ig-post-card" onClick={onClick}>
      <div className="ig-post-card__media">
        {item.media_url || item.thumbnail_url ? (
          isVideo ? (
            <video
              src={item.media_url ?? ""}
              poster={item.thumbnail_url ?? undefined}
              muted
              playsInline
            />
          ) : (
            <img
              src={item.thumbnail_url ?? item.media_url ?? ""}
              alt={item.caption || "Instagram post"}
            />
          )
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "#9ca3af",
            }}
          >
            {isVideo ? <PlayCircle size={28} /> : <FileImage size={28} />}
          </div>
        )}
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
          }}
        >
          <PostMediaBadge mediaType={item.media_type} />
        </div>
      </div>
      <div className="ig-post-card__body">
        <div className="ig-post-card__caption">
          {item.caption || "(キャプションなし)"}
        </div>
        <div
          style={{
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "var(--ig-muted)",
            fontSize: 11,
          }}
        >
          <span>{formatFullDateTime(item.timestamp)}</span>
          <span style={{ display: "inline-flex", gap: 8 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
              <Heart size={11} />
              {formatNumber(item.like_count)}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
              <MessageCircle size={11} />
              {formatNumber(item.comments_count)}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Detail modal(spec §9.3)
// ---------------------------------------------------------------------------

function PostDetailModal({
  item,
  onClose,
}: {
  item: MediaItem;
  onClose: () => void;
}) {
  const isVideo = item.media_type === "REEL" || item.media_type === "VIDEO";
  const [activeIndex, setActiveIndex] = useState(0);

  // CAROUSEL_ALBUM の場合のサブ画像:現状 ViewModel に配列は無いので 1 枚扱い。
  // 将来的に children フィールドが追加されたらここを差し替える。
  const mediaList: { url: string | null }[] = item.media_url
    ? [{ url: item.media_url }]
    : [];

  const active = mediaList[activeIndex] ?? mediaList[0] ?? { url: null };

  return (
    <div
      className="ig-post-detail-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="ig-post-detail-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="ig-post-detail-modal__header">
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background:
                  "linear-gradient(135deg, #6d3be8 0%, #d9369f 56%, #ff9f2f)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <ImageIcon size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "var(--ig-text)",
                }}
              >
                投稿詳細
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--ig-muted)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {MEDIA_TYPE_LABEL[item.media_type]}・{formatFullDateTime(item.timestamp)}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {item.permalink ? (
              <a
                href={item.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="ig-button"
                style={{ height: 36, fontSize: 12 }}
              >
                <ExternalLink size={13} />
                Instagramで開く
              </a>
            ) : null}
            <button
              type="button"
              className="ig-button ig-button--ghost"
              style={{ height: 36, width: 36, padding: 0 }}
              onClick={onClose}
              aria-label="閉じる"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Main */}
        <div className="ig-post-detail-modal__content">
          {/* 左:メディア */}
          <div className="ig-post-detail-media">
            <div className="ig-post-detail-media__stage">
              {!active.url ? (
                <div
                  style={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: 13,
                    display: "grid",
                    placeItems: "center",
                    gap: 8,
                    width: "100%",
                    height: "100%",
                  }}
                >
                  {isVideo ? <PlayCircle size={48} /> : <FileImage size={48} />}
                  <span>メディアを読み込めませんでした</span>
                </div>
              ) : isVideo ? (
                <video
                  src={active.url}
                  controls
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <img
                  src={active.url}
                  alt={item.caption || "Instagram post"}
                />
              )}
            </div>

            {mediaList.length > 1 ? (
              <div className="ig-post-detail-thumbnails">
                {mediaList.map((m, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`ig-post-detail-thumbnail${
                      i === activeIndex ? " is-active" : ""
                    }`}
                    onClick={() => setActiveIndex(i)}
                  >
                    {m.url ? <img src={m.url} alt="" /> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* 右:詳細 */}
          <div className="ig-post-detail-panel">
            {/* アカウント */}
            <SectionCard title="アカウント情報">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: "var(--ig-surface-soft)",
                    color: "var(--ig-text)",
                    display: "grid",
                    placeItems: "center",
                    fontWeight: 700,
                  }}
                >
                  IG
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>@username</div>
                  <div style={{ fontSize: 10, color: "var(--ig-muted)" }}>
                    連携アカウント
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Caption */}
            <SectionCard title="キャプション">
              <div
                style={{
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: "var(--ig-text)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {item.caption || "(キャプションなし)"}
              </div>
              {item.caption ? (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 10,
                    color: "var(--ig-muted)",
                  }}
                >
                  {item.caption.match(/#[\wぁ-んァ-ヶ一-龯]+/g)?.map((t) => (
                    <span
                      key={t}
                      style={{
                        color: "var(--ig-primary)",
                        marginRight: 6,
                        fontWeight: 600,
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              ) : null}
            </SectionCard>

            {/* 投稿メタ */}
            <SectionCard title="投稿情報">
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Meta label="公開時間" value={formatFullDateTime(item.timestamp)} />
                <Meta label="投稿タイプ" value={MEDIA_TYPE_LABEL[item.media_type]} />
                {item.permalink ? (
                  <Meta
                    label="Permalink"
                    value={
                      <a
                        href={item.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "var(--ig-primary)" }}
                      >
                        開く
                      </a>
                    }
                  />
                ) : null}
              </div>
            </SectionCard>

            {/* Performance */}
            <SectionCard title="パフォーマンス">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 8,
                }}
              >
                <PerfStat
                  label="いいね"
                  value={formatNumber(item.like_count)}
                  icon={<Heart size={13} color="#dc2626" />}
                />
                <PerfStat
                  label="コメント"
                  value={formatNumber(item.comments_count)}
                  icon={<MessageCircle size={13} color="#4f46e5" />}
                />
              </div>
            </SectionCard>

            {/* 最近コメント(メディア単位では取れないので ViewModel がある時のみ表示) */}
            {item.comments_count > 0 ? (
              <SectionCard title="この投稿へのコメント">
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--ig-muted)",
                    textAlign: "center",
                    padding: 8,
                  }}
                >
                  コメント一覧は「コメント管理」タブから確認できます
                </div>
              </SectionCard>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "100px 1fr",
        gap: 12,
        fontSize: 12,
      }}
    >
      <span style={{ color: "var(--ig-muted)" }}>{label}</span>
      <span style={{ color: "var(--ig-text)" }}>{value}</span>
    </div>
  );
}

function PerfStat({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: "var(--ig-surface-soft)",
        borderRadius: 10,
        padding: "10px 12px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--ig-muted)",
          fontSize: 10,
        }}
      >
        {icon}
        {label}
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: "var(--ig-text)",
          marginTop: 4,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function InstagramContentPage() {
  const [activeType, setActiveType] = useState<"all" | MediaType>("all");
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState<MediaItem | null>(null);
  const [isComposerOpen, setComposerOpen] = useState(false);
  const [draftsRefreshKey, setDraftsRefreshKey] = useState(0);

  const media = useInstagramMedia();
  const drafts = useInstagramDrafts();

  const items = useMemo(() => {
    const all = media.data?.items ?? [];
    return all.filter((it) => {
      if (activeType !== "all" && it.media_type !== activeType) return false;
      if (search && !it.caption?.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [media.data, activeType, search]);

  return (
    <div className="ig-page">
      <ContentHero
        accountName={null}
        connected={Boolean(media.data?.items?.length)}
        onRefresh={media.refresh}
        loading={media.loading}
      />

      {/* Filter / 検索 / 新規投稿 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 6, flex: 1, minWidth: 200 }}>
          <button
            type="button"
            className={`ig-segmented__item${
              activeType === "all" ? " is-active" : ""
            }`}
            onClick={() => setActiveType("all")}
            style={{ height: 34 }}
          >
            すべて
          </button>
          {(["IMAGE", "VIDEO", "REEL", "CAROUSEL_ALBUM"] as MediaType[]).map(
            (t) => (
              <button
                key={t}
                type="button"
                className={`ig-segmented__item${
                  activeType === t ? " is-active" : ""
                }`}
                onClick={() => setActiveType(t)}
                style={{ height: 34 }}
              >
                {MEDIA_TYPE_LABEL[t]}
              </button>
            ),
          )}
        </div>
        <div style={{ position: "relative", flex: "0 0 260px" }}>
          <Search
            size={14}
            style={{
              position: "absolute",
              top: 13,
              left: 10,
              color: "var(--ig-muted)",
            }}
          />
          <input
            className="ig-input"
            placeholder="キャプションで検索"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 30 }}
          />
        </div>
        <button
          type="button"
          className="ig-gradient-button"
          onClick={() => setComposerOpen(true)}
        >
          <Plus size={18} />
          新規投稿
        </button>
      </div>

      {/* Grid */}
      {media.error ? (
        <ErrorState
          message={media.error}
          code={media.errorCode}
          onRetry={media.refresh}
        />
      ) : media.loading && items.length === 0 ? (
        <LoadingState label="投稿を読み込んでいます..." />
      ) : items.length === 0 ? (
        <EmptyState
          title="該当する投稿がありません"
          description={
            search || activeType !== "all"
              ? "条件を変えて再度お試しください。"
              : "このアカウントにはまだ公開された投稿がありません。"
          }
        />
      ) : (
        <div className="ig-content-grid">
          {items.map((it) => (
            <PostCard
              key={it.id}
              item={it}
              onClick={() => setSelectedItem(it)}
            />
          ))}
        </div>
      )}

      {/* Drafts */}
      <DraftsSection
        key={draftsRefreshKey}
        refresh={() => drafts.refresh()}
      />

      {selectedItem ? (
        <PostDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      ) : null}

      <InstagramPostComposerModal
        open={isComposerOpen}
        onClose={() => setComposerOpen(false)}
        onSaved={() => {
          setComposerOpen(false);
          setDraftsRefreshKey((k) => k + 1);
          media.refresh();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drafts list
// ---------------------------------------------------------------------------

function DraftsSection({ refresh }: { refresh: () => void }) {
  const drafts = useInstagramDrafts();
  const items = drafts.data?.items ?? [];
  const [publishing, setPublishing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePublish = async (id: string) => {
    setPublishing(id);
    setError(null);
    try {
      await instagramApi.publishDraft(id);
      await refresh();
    } catch (e) {
      setError(`公開に失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setPublishing(null);
    }
  };

  if (drafts.loading && items.length === 0) {
    return <LoadingState label="下書きを読み込み中..." />;
  }
  if (items.length === 0) return null;

  return (
    <SectionCard
      title="下書き一覧"
      subtitle={`未公開 ${items.filter((d) => d.status !== "published").length} 件 / 公開済 ${
        items.filter((d) => d.status === "published").length
      } 件`}
      actions={
        <button
          type="button"
          className="ig-button ig-button--ghost"
          onClick={refresh}
          style={{ height: 32 }}
        >
          更新
        </button>
      }
    >
      {error ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--ig-danger)",
            padding: "8px 10px",
            background: "var(--ig-danger-soft)",
            borderRadius: 8,
            marginBottom: 10,
          }}
        >
          {error}
        </div>
      ) : null}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {items.map((d) => (
          <DraftRow
            key={d.id}
            draft={d}
            publishing={publishing === d.id}
            onPublish={() => handlePublish(d.id)}
          />
        ))}
      </div>
    </SectionCard>
  );
}

const STATUS_META: Record<
  ContentDraft["status"],
  { label: string; bg: string; color: string }
> = {
  draft: { label: "下書き", bg: "#f1f5f9", color: "#475569" },
  scheduled: { label: "予約", bg: "#f4f0ff", color: "#6d28d9" },
  publishing: { label: "公開中...", bg: "#fff7e6", color: "#a9680b" },
  published: { label: "公開済", bg: "#ecfdf3", color: "#15803d" },
  failed: { label: "失敗", bg: "#fff1f2", color: "#b91c1c" },
};

function DraftRow({
  draft,
  publishing,
  onPublish,
}: {
  draft: ContentDraft;
  publishing: boolean;
  onPublish: () => void;
}) {
  const url = draft.media_items?.[0]?.url;
  const meta = STATUS_META[draft.status] ?? STATUS_META.draft;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 0",
        borderBottom: "1px solid var(--ig-divider)",
      }}
    >
      {url ? (
        <img
          src={url}
          alt=""
          style={{
            width: 56,
            height: 56,
            objectFit: "cover",
            borderRadius: 8,
            background: "#f3f4f6",
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      ) : (
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 8,
            background: "#f3f4f6",
            color: "var(--ig-muted)",
            display: "grid",
            placeItems: "center",
            fontSize: 10,
          }}
        >
          画像なし
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            color: "var(--ig-text)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.5,
          }}
        >
          {draft.caption || "(キャプションなし)"}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 6,
          }}
        >
          <span
            className="ig-badge"
            style={{ background: meta.bg, color: meta.color }}
          >
            {meta.label}
          </span>
          {draft.error_message ? (
            <span style={{ fontSize: 11, color: "var(--ig-danger)" }}>
              {draft.error_message}
            </span>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        className="ig-button ig-button--primary"
        style={{ height: 32, fontSize: 12 }}
        disabled={draft.status === "published" || publishing}
        onClick={onPublish}
      >
        {publishing
          ? "公開中..."
          : draft.status === "published"
          ? "公開済"
          : "公開する"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function ContentHero({
  accountName,
  connected,
  onRefresh,
  loading,
}: {
  accountName: string | null;
  connected: boolean;
  onRefresh: () => void;
  loading?: boolean;
}) {
  return (
    <div className="ig-module-hero">
      <div className="ig-module-hero__row">
        <div className="ig-module-hero__main">
          <div className="ig-module-hero__title">投稿管理</div>
          <div className="ig-module-hero__subtitle">
            公開済み・予約・下書きを一覧で管理
          </div>
        </div>
        <div className="ig-module-hero__actions">
          <AccountChip accountName={accountName} connected={connected} />
          <RefreshButton onClick={onRefresh} loading={loading} />
        </div>
      </div>
      <InstagramSubnav variant="hero" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// useMemo import は create タブに既に必要
// ---------------------------------------------------------------------------
import { useMemo } from "react";
